import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { Sparkles, Copy as CopyIcon, X, Trash2, Calendar as CalendarIcon, Link2 } from "lucide-react";
import { toast } from "sonner";
import Markdown from "../components/Markdown";

const PLATFORMS = ["instagram", "tiktok", "facebook", "whatsapp", "pauta"];

const CopyStudio = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState(null); // 'free' or 'from_post'
  const [params] = useSearchParams();

  useEffect(() => {
    if (params.get("new") === "1") setMode("free");
    if (params.get("from_post")) setMode("from_post");
  }, [params]);

  const load = () => {
    setLoading(true);
    api.get("/copies").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  return (
    <Layout
      title="Copy Studio"
      subtitle="Genera copies desde una publicación del calendario o libremente"
      actions={
        <div className="flex gap-2">
          <button onClick={() => setMode("from_post")} data-testid="copy-from-post-btn" className="btn-outline flex items-center gap-2">
            <CalendarIcon size={14} /> Desde post del calendario
          </button>
          <button onClick={() => setMode("free")} data-testid="new-copy-btn" className="btn-primary flex items-center gap-2">
            <Sparkles size={14} /> Copy libre
          </button>
        </div>
      }
    >
      {loading ? (
        <div className="flex justify-center py-20"><span className="spinner" /></div>
      ) : items.length === 0 ? (
        <div className="aura-card-flat p-12 text-center">
          <div className="w-14 h-14 rounded-2xl bg-[#D99D26]/15 text-[#8a5e10] flex items-center justify-center mx-auto mb-4">
            <CopyIcon size={24} />
          </div>
          <h3 className="font-display text-2xl font-medium">Copy Studio listo</h3>
          <p className="text-[#666] mt-2 mb-5">Selecciona una publicación del calendario y Aura genera el copy completo, o crea uno desde cero.</p>
          <div className="flex justify-center gap-2">
            <button onClick={() => setMode("from_post")} className="btn-primary">Generar desde publicación</button>
            <button onClick={() => setMode("free")} className="btn-outline">Crear copy libre</button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((c) => <CopyCard key={c.id} item={c} onChange={load} />)}
        </div>
      )}

      {mode === "free" && <CopyForm onClose={() => setMode(null)} onCreated={() => { setMode(null); load(); }} />}
      {mode === "from_post" && <CopyFromCalendar onClose={() => setMode(null)} onCreated={() => { setMode(null); load(); }} />}
    </Layout>
  );
};

const CopyCard = ({ item, onChange }) => {
  const remove = async () => {
    if (!window.confirm("¿Eliminar este copy?")) return;
    await api.delete(`/copies/${item.id}`);
    onChange();
  };
  const copyText = (txt) => {
    navigator.clipboard.writeText(txt);
    toast.success("Copiado");
  };
  return (
    <div className="aura-card p-5 group relative" data-testid={`copy-${item.id}`}>
      <div className="flex justify-between items-start mb-3">
        <div className="flex gap-1">
          <span className="pill pill-dark">{item.platform}</span>
          {item.calendar_item_id && <span className="pill pill-yellow text-[10px] flex items-center gap-1"><Link2 size={10} /> vinculado</span>}
        </div>
        <button onClick={remove} className="opacity-0 group-hover:opacity-100 text-[#666] hover:text-red-600 transition-opacity"><Trash2 size={14} /></button>
      </div>
      <h3 className="font-display text-base font-medium mb-3">{item.title}</h3>
      {item.short && (
        <div className="mb-3">
          <div className="label-overline mb-1">Versión corta</div>
          <p className="text-sm text-[#0A0A0A] line-clamp-3">{item.short}</p>
          <button onClick={() => copyText(item.short)} className="text-xs text-[#8a5e10] mt-1 font-medium">Copiar</button>
        </div>
      )}
      {item.cta && (
        <div className="text-xs text-[#666] mt-2"><span className="font-medium">CTA:</span> {item.cta}</div>
      )}
      {item.hashtags?.length > 0 && (
        <div className="flex gap-1 mt-2 flex-wrap">
          {item.hashtags.slice(0, 5).map((h, i) => (
            <span key={i} className="text-[11px] text-[#8a5e10]"># {h}</span>
          ))}
        </div>
      )}
    </div>
  );
};

const CopyFromCalendar = ({ onClose, onCreated }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState("");
  const [extraInput, setExtraInput] = useState("");
  const [generating, setGenerating] = useState(false);
  const [output, setOutput] = useState("");

  useEffect(() => {
    api.get("/editorial/timeline", { params: { types: "calendar" } })
      .then((r) => {
        // Filter to upcoming or recent items
        const today = new Date().toISOString().slice(0, 10);
        const filtered = (r.data.items || []).filter((it) => (it.date || "") >= today.slice(0, 7))
          .sort((a, b) => (a.date || "").localeCompare(b.date || ""));
        setItems(filtered);
      })
      .finally(() => setLoading(false));
  }, []);

  const generate = async () => {
    if (!selectedId) { toast.error("Selecciona una publicación"); return; }
    setGenerating(true);
    try {
      const res = await api.post("/workflow/copies/from-calendar", {
        calendar_item_id: selectedId,
        extra_input: extraInput,
      });
      setOutput(res.data.raw_output || "");
      toast.success("Copy generado y adjuntado a la publicación");
      setTimeout(() => onCreated(), 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto p-7">
        <div className="flex justify-between items-start mb-5">
          <div>
            <div className="label-overline">Aura · Copy desde calendario</div>
            <h2 className="font-display text-2xl font-medium">Generar copy para una publicación</h2>
          </div>
          <button onClick={onClose}><X size={18} /></button>
        </div>

        {loading ? (
          <div className="py-12 text-center"><span className="spinner" /></div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="aura-label">Selecciona la publicación del calendario *</label>
              <select className="aura-input" value={selectedId} onChange={(e) => setSelectedId(e.target.value)} data-testid="cal-item-select">
                <option value="">— Elige una publicación —</option>
                {items.map((it) => (
                  <option key={it.id} value={it.id}>
                    {it.date} {it.time || ""} · {it.platform} {it.format ? `· ${it.format}` : ""} — {it.title}
                  </option>
                ))}
              </select>
              {items.length === 0 && <p className="text-xs text-[#666] mt-2">No hay publicaciones programadas. Crea una en el calendario editorial primero.</p>}
            </div>
            <div>
              <label className="aura-label">Notas extras / ángulo deseado (opcional)</label>
              <textarea className="aura-input" rows="3" value={extraInput} onChange={(e) => setExtraInput(e.target.value)} placeholder="Resalta el descuento 4x3, tono más urgente..." data-testid="copy-extra-input" />
            </div>
            <button onClick={generate} disabled={generating || !selectedId} className="btn-primary w-full justify-center flex items-center gap-2" data-testid="generate-copy-from-cal-btn">
              {generating ? <><span className="spinner" /> Aura escribiendo copy...</> : <><Sparkles size={14} /> Generar y adjuntar al post</>}
            </button>

            {output && (
              <div className="border-t border-[#E5E5E5] pt-5">
                <div className="label-overline mb-2">Copy generado (ya adjunto a la publicación)</div>
                <div className="aura-card-flat p-5 max-h-72 overflow-y-auto">
                  <Markdown content={output} />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const CopyForm = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({ title: "", platform: "instagram", user_input: "" });
  const [generating, setGenerating] = useState(false);
  const [output, setOutput] = useState("");
  const [saving, setSaving] = useState(false);

  const generate = async () => {
    if (!form.user_input.trim()) {
      toast.error("Describe la idea o promoción");
      return;
    }
    setGenerating(true);
    setOutput("");
    try {
      const prompt = `Genera copies para Rubí Perfumería para ${form.platform}. Idea/promoción: ${form.user_input}. Título: ${form.title || "sin título"}.`;
      const res = await api.post("/aura/generate", { prompt, task_type: "copy" });
      setOutput(res.data.output);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al generar");
    } finally {
      setGenerating(false);
    }
  };

  const save = async () => {
    if (!output) {
      toast.error("Genera el copy primero");
      return;
    }
    setSaving(true);
    try {
      const parseSection = (label) => {
        const re = new RegExp(`\\*\\*${label}[^*]*\\*\\*[\\s\\S]*?([\\s\\S]+?)(?=\\n\\*\\*|$)`, "i");
        const m = output.match(re);
        return m ? m[1].trim().substring(0, 600) : "";
      };
      const body = {
        ...form,
        short: parseSection("Versión corta") || output.substring(0, 300),
        emotional: parseSection("Versión emocional"),
        commercial: parseSection("Versión comercial"),
        ad: parseSection("Versión para pauta"),
        cta: parseSection("CTA"),
        hashtags: [...output.matchAll(/#(\w+)/g)].map((m) => m[1]).slice(0, 15),
      };
      await api.post("/copies", body);
      toast.success("Copy guardado");
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-7">
        <div className="flex justify-between items-start mb-5">
          <div>
            <div className="label-overline">Aura Copy</div>
            <h2 className="font-display text-2xl font-medium">Copy libre</h2>
          </div>
          <button onClick={onClose} className="text-[#666]"><X size={18} /></button>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="aura-label">Título</label>
              <input className="aura-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Promo 4x3 Tampico" data-testid="copy-title" />
            </div>
            <div>
              <label className="aura-label">Plataforma</label>
              <select className="aura-input" value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })}>
                {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="aura-label">Idea, promoción o contexto</label>
            <textarea className="aura-input" rows="4" value={form.user_input} onChange={(e) => setForm({ ...form, user_input: e.target.value })} placeholder="Promoción 4x3 en perfumes árabes este fin de semana..." data-testid="copy-input" />
          </div>
          <button onClick={generate} disabled={generating} className="btn-outline flex items-center gap-2" data-testid="generate-copy-btn">
            {generating ? <><span className="spinner" /> Aura está pensando...</> : <><Sparkles size={14} /> Generar con Aura</>}
          </button>

          {output && (
            <div className="border-t border-[#E5E5E5] pt-5 mt-4">
              <div className="label-overline mb-2">Resultado Aura</div>
              <div className="aura-card-flat p-5 max-h-96 overflow-y-auto" data-testid="copy-output">
                <Markdown content={output} />
              </div>
              <div className="flex gap-2 mt-4">
                <button onClick={() => { navigator.clipboard.writeText(output); toast.success("Copiado al portapapeles"); }} className="btn-ghost">Copiar todo</button>
                <button onClick={save} disabled={saving} className="btn-primary flex items-center gap-2" data-testid="copy-save-btn">
                  {saving && <span className="spinner" />} Guardar copy
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CopyStudio;
